// eslint-disable-next-line no-unused-vars
import React, { memo, useEffect, useMemo } from 'react';
import useSWR from 'swr';
import { node } from 'prop-types';
import { createContext, useContextSelector } from 'use-context-selector';
import noop from 'lodash/noop';

import { API_PRODUCTS } from 'Constants';
import useRouter from 'Hooks/useRouter';
import { UserContext } from 'Contexts/UserContext';
import useBreadcrumbs from 'Hooks/useBreadcrumbs';

export const ProductContext = createContext({
  data: null,
  error: null,
  isLoading: false,
  updateProduct: noop
});

const ProductProvider = memo(({ children }) => {
  const { params } = useRouter();
  const breadcrumbs = useBreadcrumbs();
  const user = useContextSelector(UserContext, data => data.data);

  const path = `${ API_PRODUCTS }/${ params.id }`;
  const response = useSWR(
    (user?.loggedIn && user?.email)
      ? `${ path }?uid=${ user.email }&currency=${ user.defaultCurrency }&vat=${ user.withVat }`
      : path,
    {
      revalidateOnMount: true,
      shouldRetryOnError: false,
      errorRetryCount: 1
    }
  );

  const {
    data: recommended,
    isValidating: recommendedLoading
  } = useSWR(
    (user?.loggedIn && user?.email)
      ? `${ API_PRODUCTS }/${ params.id }/recommended-products?uid=${ user.email }&currency=${ user.defaultCurrency }&vat=${ user.withVat }`
      : `${ API_PRODUCTS }/${ params.id }/recommended-products`,
    {
      revalidateOnMount: true
    }
  );

  const {
    data: resealed,
    isValidating: resealedLoading
  } = useSWR(
    () => {
      if (response.data?.hasResealed) {
        return (user?.loggedIn && user?.email)
          ? `${ API_PRODUCTS }/${ params.id }/resealed?uid=${ user.email }&currency=${ user.defaultCurrency }&vat=${ user.withVat }`
          : `${ API_PRODUCTS }/${ params.id }/resealed`;
      }
      return null;
    },
    {
      revalidateOnMount: response?.data?.hasResealed
    }
  );

  useEffect(() => {
    if (response.data) {
      breadcrumbs.setBreadcrumbs(prevState => ({
        ...prevState,
        product: response.data
      }));
    } else if (response.error?.info?.error?.code === 'notFound') {
      breadcrumbs.setBreadcrumbs(prevState => ({
        ...prevState,
        product: null
      }));
    }
  }, [response.data, response.error]);

  const memoizedProduct = useMemo(
    () => ({
      data: response.data
        ? {
          ...response.data,
          resealed,
          recommended
        }
        : null,
      updateProduct(data) {
        response.mutate(prevState => ({
          ...prevState,
          ...data
        }), true);
      },
      error: response.error,
      isLoading: response.isValidating,
      recommended: recommendedLoading ? null : recommended,
      recommendedLoading,
      resealedLoading
    }),
    [
      response.data,
      response.error,
      response.isValidating,
      resealed,
      recommended,
      resealedLoading,
      recommendedLoading
    ]
  );

  return (
    <ProductContext.Provider value={ memoizedProduct }>
      { children }
    </ProductContext.Provider>
  );
});

ProductProvider.propTypes = {
  children: node
};

ProductProvider.displayName = 'ProductProvider';

export default ProductProvider;
